import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Loader2 } from 'lucide-react';

// No role field here — the backend doesn't accept one at creation time, a
// new user is created role-less. Roles are assigned afterward as a
// separate action (AhedgeUsers.jsx's own "Assign Role" row action /
// AssignRoleModal), matching how the account actually comes into being.
const defaultFormData = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
};

const FieldLabel = ({ htmlFor, children, required }) => (
  <label
    htmlFor={htmlFor}
    className="block text-[12.5px] font-[560] text-foreground mb-1.5 tracking-[.005em]"
  >
    {children}
    {required && <span className="text-destructive ml-0.5">*</span>}
  </label>
);

const SectionLabel = ({ children }) => (
  <p className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-muted-foreground pt-2 pb-2">
    {children}
  </p>
);

const fieldBase =
  'w-full h-9 px-3 py-2 rounded-lg border border-input bg-card text-sm text-foreground-strong placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] disabled:opacity-50 disabled:cursor-not-allowed';

export const AddUserModal = ({ isOpen, onSubmit, onClose, loading }) => {
  const [formData, setFormData] = useState(defaultFormData);

  const set = (key) => (e) =>
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  const hasMissingRequired = () => {
    const required = ['firstName', 'lastName', 'email', 'password'];
    return required.some((f) => !formData[f]?.trim());
  };

  const isFormValid = !hasMissingRequired();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    onSubmit(formData);
  };

  const handleClose = () => {
    if (!loading) {
      setFormData(defaultFormData);
      onClose();
    }
  };

  React.useEffect(() => {
    if (!isOpen) {
      setFormData(defaultFormData);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add User</DialogTitle>
          <DialogDescription>
            Create a new Ahedge platform user account.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody>
            <SectionLabel>Personal Details</SectionLabel>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <FieldLabel htmlFor="firstName" required>
                  First Name
                </FieldLabel>
                <input
                  id="firstName"
                  className={fieldBase}
                  value={formData.firstName}
                  onChange={set('firstName')}
                  placeholder="First"
                  disabled={loading}
                />
              </div>
              <div>
                <FieldLabel htmlFor="middleName">Middle Name</FieldLabel>
                <input
                  id="middleName"
                  className={fieldBase}
                  value={formData.middleName}
                  onChange={set('middleName')}
                  placeholder="Middle (optional)"
                  disabled={loading}
                />
              </div>
              <div>
                <FieldLabel htmlFor="lastName" required>
                  Last Name
                </FieldLabel>
                <input
                  id="lastName"
                  className={fieldBase}
                  value={formData.lastName}
                  onChange={set('lastName')}
                  placeholder="Last"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="border-t border-border my-3" />

            <SectionLabel>Contact &amp; Access</SectionLabel>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <FieldLabel htmlFor="email" required>
                  Email
                </FieldLabel>
                <input
                  id="email"
                  type="email"
                  className={fieldBase}
                  value={formData.email}
                  onChange={set('email')}
                  placeholder="user@example.com"
                  disabled={loading}
                />
              </div>
              <div>
                <FieldLabel htmlFor="phone">Phone</FieldLabel>
                <input
                  id="phone"
                  type="tel"
                  className={fieldBase}
                  value={formData.phone}
                  onChange={set('phone')}
                  placeholder="+91 00000 00000 (optional)"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <FieldLabel htmlFor="password" required>
                Password
              </FieldLabel>
              <input
                id="password"
                type="password"
                className={fieldBase}
                value={formData.password}
                onChange={set('password')}
                placeholder="Set a password"
                disabled={loading}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !isFormValid}
              className="min-w-[120px]"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
